import React from 'react'
import { useQuery } from '@tanstack/react-query'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@shared/components/ui/card'
import { Button } from '@shared/components/ui/button'
import { apiUrl } from '@/lib/api'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  faUsers,
  faCreditCard,
  faChartLine,
  faExclamationTriangle,
  faPlus,
  faCog,
  faClipboardList,
  faUserPlus,
  faEdit,
  faTrash,
  faShieldAlt,
  faDatabase,
  faBell
} from '@fortawesome/free-solid-svg-icons'
import { Link } from 'react-router-dom'
import { PageHeader } from '@shared/components/ui/page-header'

interface ActivityItem {
  id: string
  timestamp: string
  user: string
  action: string
  resource: string
  details: string
  severity: string
}

const AdminDashboard: React.FC = () => {
  // Fetch stats from API
  const { data: stats = {
    totalTenants: 0,
    activeTenants: 0,
    suspendedTenants: 0,
    totalPlans: 0,
    systemHealth: 'Unknown'
  }, isLoading: statsLoading } = useQuery({
    queryKey: ['adminStats'],
    queryFn: async () => {
      const { auth } = await import('@/lib/firebase')
      const idToken = await auth.currentUser?.getIdToken()
      const response = await fetch(apiUrl('/api/admin/stats'), { headers: idToken ? { 'Authorization': `Bearer ${idToken}` } : {} })

      if (!response.ok) {
        throw new Error('Failed to fetch statistics')
      }

      return response.json()
    },
    retry: false
  })

  // Fetch recent activity from audit logs
  const { data: activityData, isLoading: activityLoading } = useQuery({
    queryKey: ['adminActivity'],
    queryFn: async () => {
      const { auth } = await import('@/lib/firebase')
      const idToken = await auth.currentUser?.getIdToken()
      if (!idToken) throw new Error('No authentication token found')

      const response = await fetch(apiUrl('/api/admin/audit-logs?limit=10'), {
        headers: {
          'Authorization': `Bearer ${idToken}`
        }
      })

      if (!response.ok) {
        throw new Error('Failed to fetch activity data')
      }

      const data = await response.json()
      return data.logs || []
    },
    retry: false,
    refetchInterval: 30000 // Refetch every 30 seconds
  })

  // Helper function to get activity icon and color
  const getActivityDisplay = (activity: ActivityItem) => {
    const { action, resource, severity } = activity

    // Determine icon and color based on action and resource
    if (action.includes('create_subscription')) {
      return { icon: faCreditCard, color: 'bg-green-500', text: 'New Subscription' }
    } else if (action.includes('cancel_subscription')) {
      return { icon: faCreditCard, color: 'bg-red-500', text: 'Subscription Canceled' }
    } else if (action.includes('modify_subscription')) {
      return { icon: faEdit, color: 'bg-blue-500', text: 'Subscription Modified' }
    } else if (action.includes('create') || action.includes('register')) {
      return { icon: faUserPlus, color: 'bg-green-500', text: 'Created' }
    } else if (action.includes('update') || action.includes('edit')) {
      return { icon: faEdit, color: 'bg-blue-500', text: 'Updated' }
    } else if (action.includes('delete') || action.includes('remove')) {
      return { icon: faTrash, color: 'bg-red-500', text: 'Deleted' }
    } else if (action.includes('login') || action.includes('auth')) {
      return { icon: faShieldAlt, color: 'bg-purple-500', text: 'Authentication' }
    } else if (action.includes('backup') || action.includes('maintenance')) {
      return { icon: faDatabase, color: 'bg-yellow-500', text: 'System' }
    } else if (action.includes('notification') || action.includes('alert')) {
      return { icon: faBell, color: 'bg-orange-500', text: 'Notification' }
    } else {
      return { icon: faCog, color: 'bg-gray-500', text: 'Activity' }
    }
  }

  // Helper function to format timestamp
  const formatTimestamp = (timestamp: string) => {
    const date = new Date(timestamp)
    const now = new Date()
    const diffMs = now.getTime() - date.getTime()
    const diffMins = Math.floor(diffMs / 60000)
    const diffHours = Math.floor(diffMins / 60)
    const diffDays = Math.floor(diffHours / 24)

    if (diffMins < 1) return 'Just now'
    if (diffMins < 60) return `${diffMins} minute${diffMins === 1 ? '' : 's'} ago`
    if (diffHours < 24) return `${diffHours} hour${diffHours === 1 ? '' : 's'} ago`
    if (diffDays < 7) return `${diffDays} day${diffDays === 1 ? '' : 's'} ago`
    return date.toLocaleDateString()
  }

  const quickActions = [
    {
      title: 'Create Tenant',
      description: 'Add a new tenant to the platform',
      icon: faPlus,
      href: '/tenants',
      color: 'bg-blue-500'
    },
    {
      title: 'Manage Plans',
      description: 'Configure subscription plans',
      icon: faCreditCard,
      href: '/plans',
      color: 'bg-green-500'
    },
    {
      title: 'System Settings',
      description: 'Configure platform settings',
      icon: faCog,
      href: '/system-settings',
      color: 'bg-purple-500'
    },
    {
      title: 'View Audit Log',
      description: 'Review system activity',
      icon: faClipboardList,
      href: '/audit-log',
      color: 'bg-orange-500'
    }
  ]

  return (
    <div className="space-y-6">
      {/* Header */}
      <PageHeader title="Admin Dashboard" description="Platform overview and management tools"></PageHeader>

      {/* Stats Cards */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Tenants</CardTitle>
            <FontAwesomeIcon icon={faUsers} className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {statsLoading ? '...' : stats.totalTenants}
            </div>
            <p className="text-xs text-muted-foreground">
              {statsLoading ? 'Loading...' : `${stats.activeTenants} active, ${stats.suspendedTenants} suspended`}
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Active Tenants</CardTitle>
            <FontAwesomeIcon icon={faChartLine} className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-green-600">
              {statsLoading ? '...' : stats.activeTenants}
            </div>
            <p className="text-xs text-muted-foreground">
              {statsLoading ? 'Loading...' : `${((stats.activeTenants / stats.totalTenants) * 100).toFixed(1)}% of total`}
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Available Plans</CardTitle>
            <FontAwesomeIcon icon={faCreditCard} className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {statsLoading ? '...' : stats.totalPlans}
            </div>
            <p className="text-xs text-muted-foreground">
              Subscription plans configured
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">System Health</CardTitle>
            <FontAwesomeIcon icon={faExclamationTriangle} className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-green-600">
              {statsLoading ? '...' : stats.systemHealth}
            </div>
            <p className="text-xs text-muted-foreground">
              All systems operational
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Quick Actions */}
      <div>
        <h2 className="text-xl font-semibold mb-4">Quick Actions</h2>
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
          {quickActions.map((action) => (
            <Card key={action.title} className="hover:shadow-md transition-shadow">
              <CardHeader>
                <div className="flex items-center space-x-2">
                  <div className={`p-2 rounded-lg ${action.color}`}>
                    <FontAwesomeIcon icon={action.icon} className="h-4 w-4 text-white" />
                  </div>
                  <CardTitle className="text-lg">{action.title}</CardTitle>
                </div>
                <CardDescription>{action.description}</CardDescription>
              </CardHeader>
              <CardContent>
                <Button asChild className="w-full">
                  <Link to={action.href}>
                    Go to {action.title}
                  </Link>
                </Button>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>

      {/* Recent Activity */}
      <div>
        <h2 className="text-xl font-semibold mb-4">Recent Activity</h2>
        <Card>
          <CardHeader>
            <CardTitle>Platform Activity</CardTitle>
            <CardDescription>
              Recent actions and system events
            </CardDescription>
          </CardHeader>
          <CardContent>
            {/*<div className="space-y-4">
              <div className="flex items-center space-x-4">
                <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                <div className="flex-1">
                  <p className="text-sm font-medium">New tenant created</p>
                  <p className="text-xs text-muted-foreground">Auto Shop Pro - 2 minutes ago</p>
                </div>
              </div>
              <div className="flex items-center space-x-4">
                <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                <div className="flex-1">
                  <p className="text-sm font-medium">Plan updated</p>
                  <p className="text-xs text-muted-foreground">Premium plan features modified - 15 minutes ago</p>
                </div>
              </div>
              <div className="flex items-center space-x-4">
                <div className="w-2 h-2 bg-yellow-500 rounded-full"></div>
                <div className="flex-1">
                  <p className="text-sm font-medium">System maintenance</p>
                  <p className="text-xs text-muted-foreground">Database backup completed - 1 hour ago</p>
                </div>
              </div>
              <div className="flex items-center space-x-4">
                <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                <div className="flex-1">
                  <p className="text-sm font-medium">Tenant activated</p>
                  <p className="text-xs text-muted-foreground">Quick Fix Garage - 2 hours ago</p>
                </div>
              </div>
            </div>*/}

            {activityLoading ? (
              <div className="flex items-center justify-center py-8">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary"></div>
                <span className="ml-2 text-sm text-muted-foreground">Loading activity...</span>
              </div>
            ) : activityData && activityData.length > 0 ? (
              <div className="space-y-4">
                {activityData.map((activity: ActivityItem) => {
                  const display = getActivityDisplay(activity);
                  const details = activity.details ? JSON.parse(activity.details) : {};
                  const resourceName = details.name || details.tenant_name || activity.resource || 'Unknown';

                  //console.log(display, details, resourceName);
                  
                  // Create more descriptive text for subscription events
                  let description = `${display.text} ${activity.resource}`
                  if (activity.action.includes('subscription')) {
                    if (activity.action === 'cancel_subscription' || activity.action === 'modify_subscription') {
                      // For cancellations and modifications, show "for {tenant name}"
                      if (details.tenantName) {
                        description = `${display.text} for ${details.tenantName}`
                      } else {
                        description = display.text
                      }
                    } else if (activity.action === 'create_subscription') {
                      // For new subscriptions, show tenant name if available
                      if (details.tenantName) {
                        description = `${display.text} for ${details.tenantName}`
                      } else if (details.planId) {
                        description = `${display.text} (Plan ID: ${details.planId})`
                      } else {
                        description = display.text
                      }
                    }
                  } else if (resourceName !== 'Unknown') {
                    description += ` - ${resourceName}`
                  }
                  
                  return (
                    <div key={activity.id} className="flex items-center space-x-4">
                      <div className={`w-2 h-2 ${display.color} rounded-full`}></div>
                      <div className="flex-1">
                        <p className="text-sm font-medium">
                          {description}
                        </p>
                        <p className="text-xs text-muted-foreground">
                          {activity.user} • {formatTimestamp(activity.timestamp)}
                        </p>
                      </div>
                    </div>
                  )
                })}
              </div>
            ) : (
              <div className="text-center py-8">
                <FontAwesomeIcon icon={faClipboardList} className="h-8 w-8 text-muted-foreground mb-2" />
                <p className="text-sm text-muted-foreground">No recent activity found</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  )
}

export default AdminDashboard
